import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Calendar, Truck, User, ChevronRight, RefreshCw } from 'lucide-react';
import axios from '../../../axiosInstance';

const InspectionReportsList = () => {
  const navigate = useNavigate();
  const [inspections, setInspections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const customerCode = localStorage.getItem('userId');

  useEffect(() => {
    fetchInspections();
  }, []);

  const fetchInspections = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/general-inspection/client/${customerCode}`
      );

      if (response.data.status) {
        setInspections(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load inspections');
      }
    } catch (err) {
      console.error('Error fetching inspections:', err);
      setError('Unable to load inspection reports. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'submitted':
        return 'bg-blue-100 text-blue-700';
      case 'reviewed':
        return 'bg-yellow-100 text-yellow-700';
      case 'approved':
        return 'bg-green-100 text-green-700';
      case 'rejected':
        return 'bg-red-100 text-red-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const handleViewReport = (inspectionCode) => {
    navigate(`/inspection-report/${inspectionCode}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="animate-spin text-blue-600" size={40} />
          <p className="text-gray-600 font-medium">Loading your inspection reports...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 font-satoshi">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-gray-100 rounded-full transition"
            >
              <ArrowLeft size={24} className="text-gray-700" />
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-800">Inspection Reports</h1>
              <p className="text-sm text-gray-600">View all your vehicle inspection reports</p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <p className="text-red-700 text-sm">{error}</p>
            <button
              onClick={fetchInspections}
              className="mt-2 text-red-600 font-semibold text-sm hover:underline"
            >
              Try Again
            </button>
          </div>
        )}

        {!error && inspections.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FileText size={32} className="text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">No Inspection Reports</h3>
            <p className="text-gray-600 text-sm">
              You don't have any vehicle inspection reports yet.
            </p>
          </div>
        )}

        {inspections.length > 0 && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {inspections.map((inspection) => (
              <div
                key={inspection.inspectionCode}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-100 cursor-pointer"
                onClick={() => handleViewReport(inspection.inspectionCode)}
              >
                <div className="p-5">
                  {/* Status Badge */}
                  <div className="flex items-center justify-between mb-3">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(inspection.status)}`}>
                      {inspection.status?.toUpperCase()}
                    </span>
                    <ChevronRight size={20} className="text-gray-400" />
                  </div>

                  {/* Vehicle Info */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2">
                      <Truck size={18} className="text-blue-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-500">Vehicle Number</p>
                        <p className="font-bold text-gray-900 uppercase truncate">
                          {inspection.vehicleNo || 'N/A'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <User size={18} className="text-green-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-500">Driver Name</p>
                        <p className="font-semibold text-gray-800 truncate">
                          {inspection.driverName || 'N/A'}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <Calendar size={18} className="text-purple-600 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-500">Inspection Date</p>
                        <p className="font-semibold text-gray-800">
                          {formatDate(inspection.inspectionDate)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Model & Type */}
                  <div className="border-t border-gray-100 pt-3 mt-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">
                        {inspection.vehicleModel || 'Model N/A'}
                      </span>
                      <span className="text-gray-600 font-medium">
                        {inspection.vehicleType || 'Type N/A'}
                      </span>
                    </div>
                  </div>

                  {/* Submission Info */}
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>
                        Submitted: {formatDate(inspection.systemDate)}
                      </span>
                      <span className="capitalize">
                        by {inspection.submittedByType}
                      </span>
                    </div>
                  </div>
                </div>

                {/* View Button */}
                <div className="bg-gradient-to-r from-blue-600 to-cyan-600 p-3 text-center">
                  <span className="text-white font-semibold text-sm">
                    View Full Report
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default InspectionReportsList;
